import { Link, useNavigate, useParams } from "react-router-dom"
import useTargetStorage from "@/hooks/useTargetStorage"
import Callout from "components/callout/callout"
import DeletedDocCallout from "@/components/callout/deletedDocCallout"
import { useTranslation } from "react-i18next"
import useCustomers from "hooks/useCustomers"
import { textToHTML } from "utils/converters"
import Section from "components/layout/section"
import SectionHeader from "components/layout/sectionHeader"
import SectionHeaderTitle from "components/layout/sectionHeaderTitle"
import PageTitle from "features/navigation/pageTitle"
import RightSidebarCustomer from "@/components/sidebar/right/RightSidebarCustomer"
import TableList from "components/list/tableList/tableList"
import TableListRow from "components/list/tableList/tableListRow"
import TableListCell from "components/list/tableList/tableListCell"
import useStorages from "@/hooks/useStorages"
import { paramGuardStorage, paramGuardCustomer } from "@/utils/paramGuard"
import { AccessRole, DeviceSize, FormStatus } from "@/types"
import useLayout from "@/hooks/useLayout"
import useAccess from "@/hooks/useAccess"
import useWindowDimensions from "@/hooks/useWindowDimensions"
import { useState } from "react"
import { MainSheetHeaderActions } from "@/components/layout/layoutChrome"
import FloatingButtonGroup from "@/components/buttons/floatingButtonGroup"
import FloatingButtonGroupButton from "@/components/buttons/FloatingButtonGroupButton"
import FloatingButtonGroupHr from "@/components/buttons/FloatingButtonGroupHr"
import IconButton from "@/components/buttons/iconButton"
import { ReactComponent as MoreVertIcon } from "assets/icons/more_vert.svg"

const Customer = () => {

    const { t } = useTranslation()
    const navigate = useNavigate()
    const { displayIfSize } = useWindowDimensions()
    const { targetStorageDocId: storageId } = useTargetStorage()
    const { customerId } = useParams<{ customerId: string }>()

    const { 
        storage,
        storageStatus,
        isCustomersEnabled
    } = useStorages({ storageId })
    
    const { 
        customer, 
        customerStatus,
        restoreCustomerAsync,
        isRestoring
    } = useCustomers({ storageId, customerId })

    const { addToast, openDialog } = useLayout()
    const { limitToRoles } = useAccess({ storageId: storageId || '' })
    const [isRestoringState, setIsRestoringState] = useState(false)

    const isLoading = customerStatus === FormStatus.LOADING || customer?.customerId === undefined;

    console.log("customer: ", customer);

    // Check params and declare safe ones to use in JSX
    if (
        !paramGuardStorage({storageId, storage, storageStatus, navigate}) ||
        !paramGuardCustomer({storageId, customerId, customer, customerStatus, navigate})
    ) {
        return <></>
    }
    const safeStorageId = storageId!;
    const safeCustomerId = customerId!;

    // Redirect if customers are disabled for this storage
    if (!isCustomersEnabled()) {
        navigate(`/storage`);
        return <></>;
    }

    const handleRestoreClick = async () => {
        setIsRestoringState(true)
        try {
            await restoreCustomerAsync({
                storageId: safeStorageId,
                customerId: safeCustomerId
            })
            addToast({
                text: t('customerRestored'),
                colorScheme: 'green'
            })
        } catch (error) {
            addToast({
                text: t('failedToRestore'),
                colorScheme: 'red'
            })
        } finally {
            setIsRestoringState(false)
        }
    }

    return (
        <>
            <PageTitle title={customer?.name ? t('titleCustomer_', {name: customer?.name, id: customer.customerId}) : t('titleCustomer')} />
            <MainSheetHeaderActions>
                {displayIfSize([DeviceSize.SM]) && customer &&
                    <FloatingButtonGroup id={`customer_edit_${safeCustomerId}`} openingButton={<IconButton icon={<MoreVertIcon />} />}>
                        <FloatingButtonGroupButton onClick={() => openDialog('customer_history')}>{t('viewHistory')}</FloatingButtonGroupButton>
                        {limitToRoles([AccessRole.EDITOR, AccessRole.OWNER]) && !customer.isDeleted &&
                            <>
                                <FloatingButtonGroupButton>
                                    <Link to={`/storage/customers/${safeCustomerId}/edit`}>{t('edit')}</Link>
                                </FloatingButtonGroupButton>
                                <FloatingButtonGroupHr />
                                <FloatingButtonGroupButton onClick={() => openDialog('delete_customer')}>{t('delete')}</FloatingButtonGroupButton>
                            </>
                        }
                    </FloatingButtonGroup>
                }
            </MainSheetHeaderActions>
            {customer?.isDeleted && (
                <DeletedDocCallout
                    documentType="customer"
                    deletionType="soft"
                    action={handleRestoreClick}
                    isRestoring={isRestoring || isRestoringState}
                />
            )}
            
            <Section>
                <TableList width="fit">
                    {!isLoading ?
                        <>
                            <TableListRow hoverEffect={false}>
                                <TableListCell isLabel={true}><b>{t('labelCustomerEmail')}</b></TableListCell>
                                <TableListCell>{customer.contactInformation?.email || "-"}</TableListCell>
                            </TableListRow>
                            <TableListRow hoverEffect={false}>
                                <TableListCell isLabel={true}><b>{t('labelCustomerPhone')}</b></TableListCell>
                                <TableListCell>{customer.contactInformation?.phone || "-"}</TableListCell>
                            </TableListRow>
                        </> 
                        :
                        <>
                            {Array.from({ length: 3 }).map((_, _index) => (
                                <TableListRow key={_index} hoverEffect={false}>
                                    <TableListCell><p className="skeleton shortest"></p></TableListCell>
                                    <TableListCell><p className="skeleton short"></p></TableListCell>
                                </TableListRow>
                            ))}
                            <TableListRow hoverEffect={false}>
                                <TableListCell><p className="skeleton shortest"></p></TableListCell>
                                <TableListCell>
                                    <div className="grid gap-y-2">
                                        <p className="skeleton long"></p>
                                        <p className="skeleton medium"></p>
                                        <p className="skeleton short"></p>
                                    </div>
                                </TableListCell>
                            </TableListRow>
                        </>
                        }
                </TableList>
            </Section>

            {!isLoading ? customer.notes && (
                <Section>
                    <h3>{t('labelCustomerNotes')}</h3>
                    <p dangerouslySetInnerHTML={{ __html: textToHTML(customer.notes) }} />
                </Section>
            ) :
                <Section>
                    <h3 className="skeleton short"></h3>
                    <p className="skeleton long"></p>
                    <p className="skeleton medium"></p>
                    <p className="skeleton short"></p>
                </Section>
            }

            <RightSidebarCustomer storageId={safeStorageId} customerId={safeCustomerId} />
        </>
    )
}

export default Customer
